import { useEffect, useState } from "react";
import api from "@/api/client";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";

export default function NotificationsPage() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const { data } = await api.get("/notifications", { params: { unread_only: filter === "unread", limit: 200 } });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); };
  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    toast.success("All marked read");
    load();
  };

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><Bell size={12}/> Employee alerts</div>
          <h1 className="text-4xl font-display mt-1">Notifications.</h1>
          <p className="text-sm text-muted-foreground mt-1">Every inward parcel triggers an alert for the intended employee.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="inline-flex border border-border text-xs">
            {["all","unread"].map(f => (
              <button key={f} data-testid={`notif-filter-${f}`} onClick={()=>setFilter(f)}
                className={`px-3 py-1 uppercase tracking-widest ${filter===f ? "bg-primary text-primary-foreground" : ""}`}>{f}</button>
            ))}
          </div>
          <Button data-testid="notif-mark-all-page" onClick={markAll} className="rounded-md bg-foreground text-background hover-brut shadow-brut">
            Mark all read
          </Button>
        </div>
      </header>

      <div className="border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left overline">
              <th className="p-4"></th>
              <th className="p-4">Recipient</th>
              <th className="p-4">Message</th>
              <th className="p-4">Tracking</th>
              <th className="p-4">Courier</th>
              <th className="p-4">When</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody data-testid="notifications-table">
            {data.items.length === 0 && (
              <tr><td colSpan={7} className="p-0">
                <EmptyState
                  icon={Bell}
                  variant="bell"
                  title="All caught up"
                  subtitle={filter === "unread" ? "No unread alerts right now." : "Employee notifications will appear here."}
                  testid="empty-notifications"
                />
              </td></tr>
            )}
            {data.items.map(n => (
              <tr key={n.id} className={`border-b border-border ${!n.read ? "bg-primary/5" : ""}`}>
                <td className="p-4 w-4">{!n.read && <span className="inline-block w-2 h-2 rounded-full bg-primary" />}</td>
                <td className="p-4 font-semibold">{n.recipient_name}</td>
                <td className="p-4 text-muted-foreground">{n.message}</td>
                <td className="p-4 mono text-xs">{n.tracking_number}</td>
                <td className="p-4 mono">{n.courier}</td>
                <td className="p-4 text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()}</td>
                <td className="p-4">
                  {!n.read && (
                    <button data-testid={`notif-read-${n.id}`} onClick={() => markRead(n.id)}
                      className="text-xs mono uppercase tracking-widest hover:text-primary">read →</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
